import Component from '@bythepixel/component-loader';
import windowMatchMedia from '../../constants/windowMatchMedia';

const DEFAULTS = {
  mobileNavTrigger: 'data-mobile-menu-trigger',
  mobileNavActiveClass: 'page-header--active',
  mobileContainer: 'page-header__container',
  mobileMainPanel: '[data-mobile-panel-content="Main"]',
  mobileTop: '[data-mobile-top]',
  subNavMenu: 'data-subnav-menu',
  subNavActiveClass: 'page-header__subnav-active',
  subNavListItem: '[data-sub-nav-list-item]',
  subNavCloseBtn: 'data-close-subnav',
  mobilePanelTrigger: 'data-mobile-panel-trigger',
  mobilePanelContent: 'data-mobile-panel-content',
  activeClass: 'active',
  hideClass: 'hide',
};

export default class NavigationHeader extends Component {
  static selector = '[data-comp-navigationheader]';
  #navTabs

  // Only set this to true once the hamburger icon has been clicked the first
  // time and the mobile panel offset from the top has been calculated
  #mobilePanelOffsetSet = false;

  #state = {
    activePanel: '',
    mobileNavOpen: false,
    desktopSubNavOpen: false,
  }

  constructor (el, options = DEFAULTS) {
    super(el, options);
    this.el = el;
    Object.assign(this.options = {}, DEFAULTS, options);
    this.subNavListItems = this.$container.querySelectorAll(this.options.subNavListItem);
    this.subNavMenu = this.$container.querySelector(`[${this.options.subNavMenu}]`);
    this.subNavCloseBtns = this.$container.querySelectorAll(`[${this.options.subNavCloseBtn}]`);
    this.mobileNavTrigger = this.$container.querySelector(`[${this.options.mobileNavTrigger}]`);
    this.mobileContainer = this.$container.querySelector(`.${this.options.mobileContainer}`);
    this.mobileTop = this.$container.querySelector(this.options.mobileTop);
    this.mobileMainPanel = this.$container.querySelector(this.options.mobileMainPanel);
    this.init();
  }

  init() {
    windowMatchMedia['break-3'].addListener(() => {
      this.navSetup();
    });
    this.navSetup();
  }

  navSetup() {
    const isMobile = !windowMatchMedia['break-3'].matches;

    this.clearListeners();
    this.clearStyles();
    if (isMobile) {
      this.initMobileNav();
    } else {
      this.initDesktopNav();
    }
  }

  initDesktopNav() {
    this.addDesktopSubNavListeners();
    this.addSubNavCloseButtonListeners();
    this.addSubNavEscapeKeyListener();
  }

  addDesktopSubNavListeners() {
    this.desktopSubNavHandler = this.handleDesktopSubNavClick.bind(this);

    this.subNavListItems.forEach(subNavListItem => {
      subNavListItem.addEventListener('click', this.desktopSubNavHandler);
    });
  }

  removeDesktopSubNavListeners() {
    this.subNavListItems.forEach(subNavListItem => {
      subNavListItem.removeEventListener('click', this.desktopSubNavHandler);
    });
  }

  handleDesktopSubNavClick(event) {
    const subNavIdentifier = event.currentTarget.getAttribute('data-mobile-panel-trigger');
    this.toggleDesktopSubNavMenu(subNavIdentifier);
  }

  toggleDesktopSubNavMenu(subNavIdentifier) {
    if (!this.#state.desktopSubNavOpen) {
      const subNav = this.$container.querySelector(`[${this.options.subNavMenu}="${subNavIdentifier}"]`);
      subNav.classList.add(this.options.subNavActiveClass);
      this.#state.desktopSubNavOpen = true;
      this.toggleBodyStyle();
      this.offsetDesktopSubNav(subNav);
      return;
    }
    this.#state.desktopSubNavOpen = false;
    this.toggleBodyStyle();
  }

  offsetDesktopSubNav(subNav) {
    // The header sits below the sticky announcement banner, so a 100vh subnav
    // overflows the viewport by that much. Measured after the body is fixed,
    // when the page is at the top.
    subNav.style.setProperty('--subnav-offset', `${this.el.getBoundingClientRect().top}px`);
  }

  removeSubNavActiveClass() {
    const subNavMenus = this.$container.querySelectorAll(`[${this.options.subNavMenu}]`);
    subNavMenus.forEach(subNav => {
      subNav.classList.remove(this.options.subNavActiveClass);
    });
  }

  addSubNavCloseButtonListeners() {
    this.subNavCloseHandler = this.handleSubNavCloseClick.bind(this);

    this.subNavCloseBtns.forEach(button => {
      button.addEventListener('click', this.subNavCloseHandler);
    });
  }

  removeSubNavCloseButtonListeners() {
    this.subNavCloseBtns.forEach(button => {
      button.removeEventListener('click', this.subNavCloseHandler);
    });
  }

  handleSubNavCloseClick(event) {
    const subNavIdentifier = event.currentTarget.getAttribute(this.options.subNavCloseBtn);
    const subNav = this.$container.querySelector(`[${this.options.subNavMenu}="${subNavIdentifier}"]`);
    this.closeSubNav(subNav);
  }

  addSubNavEscapeKeyListener() {
    this.subNavEscapeHandler = this.handleSubNavEscapeKey.bind(this);

    document.addEventListener('keydown', this.subNavEscapeHandler);
  }

  removeSubNavEscapeKeyListener() {
    document.removeEventListener('keydown', this.subNavEscapeHandler);
  }

  handleSubNavEscapeKey(event) {
    if (event.keyCode === 27) {
      if (this.#state.desktopSubNavOpen && windowMatchMedia['break-3'].matches) {
        const subNav = this.$container.querySelector(`.${this.options.subNavActiveClass}`);
        this.closeSubNav(subNav);
      }
    }
  }

  closeSubNav(subNav) {
    subNav.classList.remove(this.options.subNavActiveClass);
    this.#state.desktopSubNavOpen = false;
    this.toggleBodyStyle();
  }

  initMobileNav() {
    this.addMobileNavListener();
  }

  addMobileNavListener() {
    this.removeMobileNavHandler = this.handleMobileNavClick.bind(this);

    this.mobileNavTrigger.addEventListener('click', this.removeMobileNavHandler);
    this.mobileContainer.addEventListener('click', this.removeMobileNavHandler);
  }

  removeMobileNavTriggerListener() {
    this.mobileNavTrigger.removeEventListener('click', this.removeMobileNavHandler);
    this.mobileContainer.removeEventListener('click', this.removeMobileNavHandler);
  }

  addMobileScrollListener() {
    this.removeScrollHandler = this.handleMobileNavScroll.bind(this);

    window.addEventListener('scroll', this.removeScrollHandler);
  }

  removeMobileScrollListener() {
    window.removeEventListener('scroll', this.removeScrollHandler);
  }

  clearListeners() {
    this.removeMobileNavTriggerListener();
    this.removeMobileScrollListener();
    this.removeDesktopSubNavListeners();
    this.removeSubNavCloseButtonListeners();
    this.removeSubNavEscapeKeyListener();
    this.mobileContainer.style.top = null;
    this.#navTabs = null;
  }

  clearStyles() {
    if (this.el.classList.contains(this.options.activeClass)) {
      this.el.classList.remove(this.options.activeClass);
    }

    if (this.#state.activePanel) {
      this.#state.activePanel.classList.remove(this.options.activeClass);
    }

    this.removeSubNavActiveClass();
    this.el.classList.remove(this.options.mobileNavActiveClass);
    this.#state.mobileNavOpen = false;
    this.#state.desktopSubNavOpen = false;
    this.toggleBodyStyle();
  }

  offsetMobileContainer() {
    // Use the bar's bottom edge in viewport coordinates (not just its height) so
    // the fixed panel clears anything stacked above it — e.g. the sticky
    // announcement banner. Measured on open, when the page is scrolled to top.
    const mobilePanelOffset = this.mobileTop.getBoundingClientRect().bottom;

    this.mobileContainer.style.top = `${mobilePanelOffset}px`;
    this.#mobilePanelOffsetSet = true;
  }

  handleMobileNavClick(event) {
    const isMobileContainer = event.target.classList.contains(this.options.mobileContainer);
    const isMobileCloseTrigger = event.target.hasAttribute(this.options.mobileNavTrigger)
      || event.target.parentNode.hasAttribute(this.options.mobileNavTrigger);
    const isMobileNestedItem = event.target.hasAttribute(this.options.mobilePanelTrigger);

    if (isMobileNestedItem) {
      event.preventDefault();
      this.toggleMobilePanel(event.target);
      return;
    }

    // Check if the nav is open and if the user is clicking to close the nav
    if (this.#state.mobileNavOpen && (isMobileContainer || isMobileCloseTrigger)) {
      this.toggleMobileNav();
    } else if (!this.#state.mobileNavOpen) {
      this.handleMobileNavOpen();
    }
  }

  handleMobileNavScroll() {
    if (!window.pageYOffset) {
      window.requestAnimationFrame(() => {
        this.toggleMobileNav();
        this.removeMobileScrollListener();
      });
    }
  }

  handleMobileNavOpen() {
    if (!window.pageYOffset) {
      this.toggleMobileNav();
    } else {
      this.addMobileScrollListener();
      window.scroll(0, 0);
    }
  }

  toggleMobileNav() {
    this.#state.mobileNavOpen = !this.#state.mobileNavOpen;
    this.el.classList.toggle(this.options.mobileNavActiveClass);

    // Toggle the first panel's content if the mobile nav is being opened
    if (this.#state.mobileNavOpen) {
      // Position the fixed panel below the mobile-top bar now that we're
      // scrolled to the top, so its offset accounts for the sticky banner.
      if (!this.#mobilePanelOffsetSet) {
        this.offsetMobileContainer();
      }
      this.toggleBodyStyle();
      this.mobileMainPanel.classList
        .add(this.options.activeClass);
      this.#state.activePanel = this.mobileMainPanel;
    // Else reset the state of the mobile nav
    } else {
      this.clearStyles();
      this.mobileMainPanel.classList
        .remove(this.options.activeClass);
      this.#state.activePanel = '';
    }
  }

  toggleMobilePanel(panelTrigger) {
    const activePanelId = panelTrigger.getAttribute(this.options.mobilePanelTrigger);

    this.#state.activePanel.classList.remove(this.options.activeClass);
    panelTrigger.classList.remove(this.options.activeClass);
    this.#state.activePanel = this.el.querySelector(`[${this.options.mobilePanelContent}="${activePanelId}"]`);
    this.#state.activePanel.classList.add(this.options.activeClass);
    this.#state.activePanel.classList.remove(this.options.hideClass);
  }

  toggleBodyStyle() {
    if (this.#state.mobileNavOpen || this.#state.desktopSubNavOpen) {
      Object.assign(document.body.style, {
        position: 'fixed',
        height: '100%',
        width: '100%',
      });
    } else {
      Object.assign(document.body.style, {
        position: null,
        height: null,
        width: null,
      });
    }
  }
}
